'use client';
import React, { useState } from 'react';
import Badge from '@/components/ui/Badge';
import Icon from '@/components/ui/AppIcon';

type AUVStatus = 'Active' | 'Returning' | 'Idle' | 'Maintenance' | 'Deploying';

interface AUV {
  id: string;
  name: string;
  status: AUVStatus;
  lat: number;
  lon: number;
  depthM: number;
  battery: number;
  missionType: string;
  pingFreqKhz: number;
  lastContact: string;
}

const auvs: AUV[] = [
  { id: 'auv-001', name: 'Nereid-01', status: 'Active', lat: -35.44, lon: 19.21, depthM: 340, battery: 82, missionType: 'Bathymetry', pingFreqKhz: 200, lastContact: '05:03:38' },
  { id: 'auv-002', name: 'Nereid-02', status: 'Active', lat: -35.46, lon: 19.28, depthM: 320, battery: 74, missionType: 'Bathymetry', pingFreqKhz: 210, lastContact: '05:03:35' },
  { id: 'auv-003', name: 'Triton-03', status: 'Active', lat: -35.51, lon: 19.15, depthM: 410, battery: 91, missionType: 'Wildlife Survey', pingFreqKhz: 50, lastContact: '05:03:40' },
  { id: 'auv-004', name: 'Triton-04', status: 'Active', lat: -35.49, lon: 19.33, depthM: 388, battery: 67, missionType: 'Wildlife Survey', pingFreqKhz: 50, lastContact: '05:03:31' },
  { id: 'auv-005', name: 'Abyssal-05', status: 'Active', lat: -35.52, lon: 19.09, depthM: 1240, battery: 55, missionType: 'Deep Mapping', pingFreqKhz: 12, lastContact: '05:03:29' },
  { id: 'auv-006', name: 'Abyssal-06', status: 'Active', lat: -35.55, lon: 19.41, depthM: 1180, battery: 61, missionType: 'Deep Mapping', pingFreqKhz: 12, lastContact: '05:03:27' },
  { id: 'auv-007', name: 'Nereid-07', status: 'Active', lat: -35.40, lon: 19.52, depthM: 290, battery: 88, missionType: 'Distress Support', pingFreqKhz: 25, lastContact: '05:03:41' },
  { id: 'auv-008', name: 'Triton-08', status: 'Active', lat: -35.38, lon: 19.62, depthM: 265, battery: 79, missionType: 'Equipment Search', pingFreqKhz: 37, lastContact: '05:03:36' },
  { id: 'auv-009', name: 'Nereid-09', status: 'Returning', lat: -34.90, lon: 19.80, depthM: 45, battery: 21, missionType: 'Bathymetry', pingFreqKhz: 200, lastContact: '05:03:22' },
  { id: 'auv-010', name: 'Abyssal-10', status: 'Returning', lat: -34.85, lon: 19.75, depthM: 120, battery: 18, missionType: 'Deep Mapping', pingFreqKhz: 12, lastContact: '05:03:18' },
  { id: 'auv-011', name: 'Triton-11', status: 'Maintenance', lat: -34.60, lon: 19.40, depthM: 0, battery: 100, missionType: 'Standby', pingFreqKhz: 0, lastContact: '04:12:07' },
];

const statusBadge: Record<AUVStatus, 'positive' | 'info' | 'neutral' | 'negative' | 'warning'> = {
  Active: 'positive',
  Returning: 'warning',
  Idle: 'neutral',
  Maintenance: 'info',
  Deploying: 'info',
};

export default function AUVSwarmTable() {
  const [sortCol, setSortCol] = useState<keyof AUV>('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const sorted = [...auvs].sort((a, b) => {
    const av = a[sortCol];
    const bv = b[sortCol];
    if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
    return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
  });

  const toggleSort = (col: keyof AUV) => {
    if (sortCol === col) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: keyof AUV }) => (
    <Icon
      name={sortCol === col ? (sortDir === 'asc' ? 'ChevronUpIcon' : 'ChevronDownIcon') : 'ChevronUpDownIcon'}
      size={12}
      className={sortCol === col ? 'text-primary' : 'text-muted-foreground'}
    />
  );

  return (
    <div className="bg-card border border-border rounded-xl flex flex-col overflow-hidden">
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-foreground">AUV Swarm Status</h2>
          <p className="text-xs text-muted-foreground">
            {auvs.filter(a => a.status === 'Active').length} active · {auvs.filter(a => a.status === 'Returning').length} returning · {auvs.filter(a => a.status === 'Maintenance').length} maintenance
          </p>
        </div>
        <button className="flex items-center gap-1.5 px-3 py-1.5 bg-muted rounded-lg text-xs text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors border border-border">
          <Icon name="ArrowPathIcon" size={13} />
          Sync Fleet
        </button>
      </div>
      <div className="overflow-x-auto scrollbar-ocean">
        <table className="w-full text-xs min-w-[600px]">
          <thead>
            <tr className="border-b border-border">
              {([['id', 'ID'], ['name', 'Name'], ['status', 'Status'], ['depthM', 'Depth'], ['battery', 'Battery'], ['missionType', 'Mission'], ['pingFreqKhz', 'Ping kHz'], ['lastContact', 'Last Contact']] as [keyof AUV, string][]).map(([col, label]) => (
                <th
                  key={`th-auv-${col}`}
                  className="px-4 py-3 text-left text-[10px] font-semibold uppercase tracking-widest text-muted-foreground cursor-pointer hover:text-foreground transition-colors whitespace-nowrap"
                  onClick={() => toggleSort(col)}
                >
                  <span className="flex items-center gap-1">{label} <SortIcon col={col} /></span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((auv) => (
              <tr key={auv.id} className="border-b border-border/50 hover:bg-muted/40 transition-colors duration-100">
                <td className="px-4 py-2.5 font-mono text-primary text-[11px]">{auv.id}</td>
                <td className="px-4 py-2.5 font-semibold text-foreground">{auv.name}</td>
                <td className="px-4 py-2.5"><Badge variant={statusBadge[auv.status]} dot>{auv.status}</Badge></td>
                <td className="px-4 py-2.5 font-tabular text-muted-foreground">{auv.depthM > 0 ? `${auv.depthM}m` : 'Surface'}</td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <div className="w-14 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${auv.battery}%`,
                          background: auv.battery > 50 ? 'var(--positive)' : auv.battery > 25 ? 'var(--warning)' : 'var(--negative)',
                        }}
                      />
                    </div>
                    <span className={`font-tabular ${auv.battery < 25 ? 'text-negative' : auv.battery < 50 ? 'text-warning' : 'text-positive'}`}>
                      {auv.battery}%
                    </span>
                  </div>
                </td>
                <td className="px-4 py-2.5 text-foreground">{auv.missionType}</td>
                <td className="px-4 py-2.5 font-mono text-accent">{auv.pingFreqKhz > 0 ? `${auv.pingFreqKhz}` : '—'}</td>
                <td className="px-4 py-2.5 font-mono text-muted-foreground">{auv.lastContact}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}